import { useEffect, useState } from 'react'
import { Radar, Search, RotateCcw, Monitor, ChevronDown } from 'lucide-react'
import type { MapFilters, MapView, PulseRate, RegionSlug, Severity } from '@/types/map'
import { severityVar } from './map-display-utils'
import { RegionPicker } from './RegionPicker'
import './map-console.css'

export interface MapToolbarProps {
  filters: MapFilters
  onFiltersChange: (filters: MapFilters | ((prev: MapFilters) => MapFilters)) => void
  view: MapView
  onReset: () => void
  onSocToggle: () => void
  pulseRate: PulseRate
  onPulseRateChange: (rate: PulseRate) => void
  onRegionSelect: (slug: RegionSlug) => void
  onSearch: (query: string) => void
}

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']
const SEVERITY_SHORT: Record<Severity, string> = {
  critical: 'Crit',
  high: 'High',
  medium: 'Med',
  low: 'Low',
}

export function MapToolbar(props: MapToolbarProps) {
  const [regionOpen, setRegionOpen] = useState(false)
  const [q, setQ] = useState('')

  // On mount: honour prefers-reduced-motion, else restore persisted pulseRate.
  useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      if (props.pulseRate !== 'off') props.onPulseRateChange('off')
      localStorage.setItem('intelhub.map.pulseRate', 'off')
      return
    }
    const persisted = localStorage.getItem('intelhub.map.pulseRate') as PulseRate | null
    if (persisted && persisted !== props.pulseRate) props.onPulseRateChange(persisted)
  // intentionally run on mount only — deps intentionally omitted
  }, [])

  const toggleSeverity = (s: Severity) => {
    const next = new Set(props.filters.severities)
    if (next.has(s)) next.delete(s)
    else next.add(s)
    props.onFiltersChange({ ...props.filters, severities: next })
  }

  const setPulse = (p: PulseRate) => {
    localStorage.setItem('intelhub.map.pulseRate', p)
    props.onPulseRateChange(p)
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] px-3 py-2 text-xs max-[1100px]:gap-2"
      data-testid="map-toolbar"
      role="toolbar"
      aria-label="Map controls"
    >
      {/* Brand identity */}
      <div className="flex shrink-0 items-center gap-2 pr-1">
        <Radar
          className="h-4 w-4 text-[color:var(--interactive-primary)]"
          aria-hidden
        />
        <span className="font-heading text-sm font-semibold tracking-tight text-[color:var(--text-primary)]">
          INTELHUB
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
          / Threat Map
        </span>
      </div>
      <span
        className="h-5 w-px shrink-0 bg-[color:var(--border-subtle)] max-[1100px]:hidden"
        aria-hidden
      />

      {/* Search */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          props.onSearch(q)
        }}
        className="relative min-w-[180px] flex-1"
      >
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[color:var(--text-tertiary)]"
          aria-hidden
        />
        <input
          type="search"
          role="searchbox"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search region, country, target, or incident ID…"
          className="w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-primary)] py-1.5 pl-8 pr-2 text-[color:var(--text-primary)] placeholder:text-[color:var(--text-tertiary)] focus:border-[color:var(--interactive-primary)] focus:outline-none focus:ring-1 focus:ring-[color:var(--interactive-primary)]"
        />
      </form>

      {/* Severity chips */}
      <div className="flex items-center gap-1.5">
        {SEVERITIES.map((s) => {
          const on = props.filters.severities.has(s)
          return (
            <label key={s} className="cursor-pointer">
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleSeverity(s)}
                aria-label={s}
                className="peer sr-only"
              />
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[color:var(--interactive-primary)]"
                style={{
                  borderColor: on ? severityVar(s) : 'var(--border-default)',
                  color: on ? severityVar(s) : 'var(--text-tertiary)',
                  backgroundColor: on
                    ? `color-mix(in oklch, ${severityVar(s)} 14%, transparent)`
                    : 'transparent',
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: severityVar(s), opacity: on ? 1 : 0.4 }}
                />
                {SEVERITY_SHORT[s]}
              </span>
            </label>
          )
        })}
      </div>

      {/* Region picker */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setRegionOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={regionOpen}
          className="inline-flex items-center gap-1 rounded-md border border-[color:var(--border-default)] px-2.5 py-1.5 text-[color:var(--text-secondary)] transition-colors hover:border-[color:var(--interactive-primary)] hover:text-[color:var(--text-primary)]"
        >
          Region
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </button>
        {regionOpen && (
          <RegionPicker
            onSelect={(slug) => {
              setRegionOpen(false)
              props.onRegionSelect(slug)
            }}
            onReset={() => {
              setRegionOpen(false)
              props.onReset()
            }}
          />
        )}
      </div>

      <span className="flex-1 max-[1100px]:hidden" aria-hidden />

      {/* Time range */}
      <label className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
          Range
        </span>
        <input
          type="range"
          min={1}
          max={30}
          step={1}
          value={props.filters.rangeDays}
          onChange={(e) =>
            props.onFiltersChange({
              ...props.filters,
              rangeDays: Number(e.target.value),
            })
          }
          aria-label="Time range in days"
          className="w-24 accent-[color:var(--interactive-primary)]"
        />
        <span className="w-7 text-right font-mono text-[color:var(--text-secondary)]">
          -{props.filters.rangeDays}d
        </span>
      </label>

      {/* Pulse rate */}
      <label className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
          Pulse
        </span>
        <select
          aria-label="pulse-rate"
          value={props.pulseRate}
          onChange={(e) => setPulse(e.target.value as PulseRate)}
          className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-primary)] px-1.5 py-1 text-[color:var(--text-secondary)] focus:border-[color:var(--interactive-primary)] focus:outline-none"
        >
          <option value="off">Off</option>
          <option value="calm">Calm (10s)</option>
          <option value="normal">Normal (5s)</option>
          <option value="intense">Intense (3s)</option>
        </select>
      </label>

      {/* Reset */}
      <button
        type="button"
        onClick={props.onReset}
        className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-2.5 py-1.5 text-[color:var(--text-secondary)] transition-colors hover:border-[color:var(--interactive-primary)] hover:text-[color:var(--text-primary)]"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
        Reset
      </button>

      {/* SOC view */}
      <button
        type="button"
        onClick={props.onSocToggle}
        className="soc-accent-btn inline-flex items-center gap-1.5 rounded-md border border-[color:var(--interactive-primary)] px-2.5 py-1.5 font-medium uppercase tracking-[0.12em] text-[color:var(--interactive-primary)] transition-colors"
      >
        <Monitor className="h-3.5 w-3.5" aria-hidden />
        SOC View
      </button>
    </div>
  )
}
